// Zijmenu (rechts): weergave, installeren, data, snelle acties, weer & locatie.
import { useEffect, useId, useRef, useState, type DragEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  Cloud,
  CloudHail,
  CloudLightning,
  CloudRain,
  CloudSnow,
  CloudSun,
  Download,
  EyeOff,
  FileText,
  FlaskConical,
  ListChecks,
  LocateFixed,
  MapPin,
  Monitor,
  Moon,
  Palette,
  RotateCcw,
  Share2,
  Smartphone,
  Sun,
  Upload,
  Wind,
  X,
  type LucideIcon,
} from 'lucide-react';
import { AnalyticsToggle } from '../../components/AnalyticsConsent';
import { BrandLink } from '../../components/BrandLink';
import { InstallCard } from '../../components/InstallCard';
import { Modal } from '../../components/Modal';
import packageJson from '../../../package.json';
import { toExportShape } from '../../domain/activities';
import { buildShareUrl, encodeSharePayload } from '../../domain/share';
import type { ImportMode, WeatherKind, WindLevel } from '../../domain/types';
import { formatTemp, weatherCaption, windDisplay } from '../../domain/weather';
import { useDialog } from '../../hooks/useDialog';
import { useTheme } from '../../hooks/useTheme';
import { useActivities, usePrefs, useToast, useWeather } from '../../state/AppProviders';
import { useDevMode } from '../../state/devMode';
import { ActivitiesManager } from '../activities/ActivitiesManager';
import { BACKGROUND_THEMES, type BackgroundTheme, type ThemeMode } from '../../utils/themeStyles';
import { copyText, downloadJson, shareOrCopy } from './browserActions';
import {
  checkImportFile,
  detectImportFileKind,
  exportFileName,
  parseImportText,
  planImport,
  SAVE_FAILED_TEXT,
  type StatusMessage,
} from './dataTransfer';
import { btnPrimary, btnTonal, choiceClass, Disclosure, focusRing, rowButton, Section, StatusLine } from './ui';

export interface MenuDrawerProps {
  open: boolean;
  onClose: () => void;
}

const EXIT_MS = 200;

export default function MenuDrawer({ open, onClose }: MenuDrawerProps) {
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    const t = window.setTimeout(() => setMounted(false), EXIT_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  if (!mounted) return null;
  return createPortal(<DrawerPanel open={open} shown={shown} onClose={onClose} />, document.body);
}

const MODES: { id: ThemeMode; label: string; icon: LucideIcon }[] = [
  { id: 'light', label: 'Licht', icon: Sun },
  { id: 'dark', label: 'Donker', icon: Moon },
  { id: 'auto', label: 'Automatisch', icon: Monitor },
];

const WEATHER_CHIPS: { kind: WeatherKind; label: string; icon: LucideIcon }[] = [
  { kind: 'zonnig', label: 'Zon', icon: Sun },
  { kind: 'bewolkt', label: 'Bewolkt', icon: Cloud },
  { kind: 'regen', label: 'Regen', icon: CloudRain },
  { kind: 'onweer', label: 'Onweer', icon: CloudLightning },
  { kind: 'hagel', label: 'Hagel', icon: CloudHail },
  { kind: 'storm', label: 'Storm', icon: Wind },
  { kind: 'sneeuw', label: 'Sneeuw', icon: CloudSnow },
];

const WIND_CHIPS: { level: WindLevel; label: string }[] = [
  { level: 'weinig', label: 'Weinig' },
  { level: 'gemiddeld', label: 'Gemiddeld' },
  { level: 'veel', label: 'Veel' },
];

const SAMPLE_TABLE = `| Naam | Omschrijving | Type | Weer | Locatie | Duur | Energie | Aantal personen |
| --- | --- | --- | --- | --- | --- | --- | --- |
| Wingspan | Bouw de beste vogelreservaten | Binnen | Alles | | 60 | Rustig | 2, 3+ |
| Speeltuin Het Noorden | Grote buitenspeeltuin met kabelbaan | Buiten | Droog | 52.518, 6.095 | 45 | Veel energie | 1, 2, 3+ |`;

function DrawerPanel({ open, shown, onClose }: { open: boolean; shown: boolean; onClose: () => void }) {
  const panelRef = useRef<HTMLElement>(null);
  const titleId = useId();
  useDialog({ active: open, onClose, containerRef: panelRef });

  const { mode, setMode, background, setBackground } = useTheme();
  const { activities, importActivities, resetToDefaults } = useActivities();
  const { showToast } = useToast();
  const { hiddenIds, unhide, unhideAll } = usePrefs();
  const hiddenActivities = activities.filter((a) => hiddenIds.has(a.id));
  const weatherApi = useWeather();
  const { weather, isSimulated } = weatherApi;

  const [mergeMode, setMergeMode] = useState<ImportMode>('merge');
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [managing, setManaging] = useState(false);
  const { devMode, registerTap, setDevMode } = useDevMode();
  const fileRef = useRef<HTMLInputElement>(null);
  const [now] = useState(() => Date.now());

  const handleFile = async (file: File) => {
    const problem = checkImportFile(file.name, file.type, file.size);
    if (problem) return setStatus(problem);
    const kind = detectImportFileKind(file.name, file.type);
    if (!kind) return;
    setBusy(true);
    setStatus({ kind: 'info', text: `Bezig met inlezen van ${file.name}...` });
    try {
      const parsed = parseImportText(kind, await file.text());
      if (!parsed.ok) return setStatus(parsed.message);
      const plan = planImport(activities, parsed.items, mergeMode);
      if (!plan.ok) return setStatus(plan.message);
      try {
        await importActivities(plan.raw, plan.mode);
      } catch {
        return setStatus({ kind: 'error', text: SAVE_FAILED_TEXT });
      }
      setStatus({ kind: 'success', text: plan.successText });
      showToast(plan.successText);
    } catch {
      setStatus({ kind: 'error', text: 'Het bestand kon niet worden gelezen.' });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const onExport = () => {
    downloadJson(toExportShape(activities), exportFileName(new Date()));
    showToast('Activiteiten geëxporteerd.');
  };

  const onShare = async () => {
    try {
      const payload = await encodeSharePayload(activities);
      const url = buildShareUrl(window.location.origin, window.location.pathname, payload);
      const outcome = await shareOrCopy('GameOn activiteiten', url);
      if (outcome === 'copied') showToast('Deellink gekopieerd naar het klembord.');
      else if (outcome === 'shared') showToast('Deellink gedeeld.');
      else if (outcome === 'failed') showToast('Delen is niet gelukt. Probeer het opnieuw.');
    } catch {
      showToast('Delen is niet gelukt. Probeer het opnieuw.');
    }
  };

  const onReset = async () => {
    setConfirmReset(false);
    try {
      await resetToDefaults();
      showToast('Standaardactiviteiten herladen.');
    } catch {
      showToast(SAVE_FAILED_TEXT);
    }
  };

  const temp = formatTemp(weather.tempC);
  const wind = windDisplay(weather.windLevel, weather.windKmh, weather.kind === 'onbekend');
  const activeKind = isSimulated ? weather.kind : null;
  const visible = shown && open;

  return (
    <div className="fixed inset-0 z-[120]">
      <div
        aria-hidden="true"
        onClick={onClose}
        className={`absolute inset-0 bg-slate-950/50 backdrop-blur-sm transition-opacity duration-200 motion-reduce:transition-none ${visible ? 'opacity-100' : 'opacity-0'}`}
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`absolute inset-y-0 right-0 flex w-full max-w-sm flex-col bg-slate-50 text-slate-900 shadow-xl outline-none transition-transform duration-200 ease-out motion-reduce:transition-none dark:border-l dark:border-white/10 dark:bg-slate-900 dark:text-slate-100 ${visible ? 'translate-x-0' : 'translate-x-full'}`}
      >
        <header className="flex items-center justify-between gap-3 border-b border-black/5 px-5 py-3 dark:border-white/10">
          <h2 id={titleId} className="text-lg font-bold">
            Menu
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Menu sluiten"
            className={`grid h-11 w-11 place-items-center rounded-2xl bg-black/5 transition-all duration-150 active:scale-95 dark:bg-slate-800/80 ${focusRing}`}
          >
            <X size={20} aria-hidden="true" />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto overscroll-contain px-5 pb-24 pt-5">
          <Section icon={Palette} title="Weergave & Nachtmodus">
            <div role="radiogroup" aria-label="Nachtmodus" className="grid grid-cols-3 gap-2">
              {MODES.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={mode === id}
                  onClick={() => setMode(id)}
                  className={`${choiceClass(mode === id)} flex-col !gap-1 !px-1 py-2 text-xs`}
                >
                  <Icon size={18} aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>
            <div role="radiogroup" aria-label="Achtergrond" className="grid grid-cols-3 gap-3">
              {(Object.keys(BACKGROUND_THEMES) as BackgroundTheme[]).map((id) => {
                const t = BACKGROUND_THEMES[id];
                const active = background === id;
                return (
                  <button
                    key={id}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setBackground(id)}
                    className={`flex min-h-11 flex-col items-center gap-1.5 rounded-2xl p-1.5 text-xs font-semibold transition-all duration-150 active:scale-95 ${focusRing} ${active ? 'bg-black/10 dark:bg-white/15' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}
                  >
                    <span className={`block h-10 w-full rounded-xl ${t.swatch} ${active ? 'ring-2 ring-slate-900 dark:ring-slate-100' : 'ring-1 ring-black/10 dark:ring-white/20'}`} />
                    <span className="whitespace-nowrap">{t.label}</span>
                  </button>
                );
              })}
            </div>
          </Section>

          <AnalyticsToggle />

          <Section icon={Smartphone} title="Voeg toe aan beginscherm">
            <InstallCard />
          </Section>

          <Section icon={ListChecks} title="Activiteiten beheren" intro="Voeg je eigen activiteiten toe, pas ze aan of haal ze weg.">
            <button type="button" onClick={() => setManaging(true)} className={`${btnPrimary} w-full`}>
              <ListChecks size={18} aria-hidden="true" /> Mijn activiteiten
            </button>
          </Section>

          <Section
            icon={Upload}
            title="Data importeren & exporteren"
            intro="Laad een .json-backup of een .md-tabel (maximaal 2 MB)."
          >
            <fieldset className="space-y-1">
              <legend className="sr-only">Bij importeren</legend>
              {(
                [
                  ['merge', 'Samenvoegen met huidige gegevens'],
                  ['overwrite', 'Bestaande gegevens overschrijven'],
                ] as [ImportMode, string][]
              ).map(([id, label]) => (
                <label key={id} className="flex min-h-11 cursor-pointer items-center gap-3 rounded-2xl px-2 text-sm font-semibold">
                  <input
                    type="radio"
                    name="import-mode"
                    checked={mergeMode === id}
                    onChange={() => setMergeMode(id)}
                    className="h-5 w-5 shrink-0 accent-slate-900 dark:accent-slate-100"
                  />
                  {label}
                </label>
              ))}
            </fieldset>
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={`rounded-2xl p-4 text-center transition-colors duration-150 ${dragging ? 'bg-black/10 dark:bg-white/15' : 'bg-black/5 dark:border dark:border-white/10 dark:bg-slate-800/80'}`}
            >
              <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">Sleep een bestand hierheen of kies er een.</p>
              <input
                ref={fileRef}
                type="file"
                accept=".json,.md,.markdown,.txt,application/json,text/markdown"
                className="sr-only"
                tabIndex={-1}
                aria-label="Bestand kiezen"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void handleFile(f);
                }}
              />
              <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className={btnPrimary}>
                <Upload size={18} aria-hidden="true" /> Kies bestand
              </button>
            </div>
            <StatusLine status={status} />
          </Section>

          {hiddenActivities.length > 0 ? (
            <Section icon={EyeOff} title="Verborgen activiteiten" intro="Deze activiteiten krijg je niet meer als suggestie.">
              <ul className="space-y-1" aria-label="Verborgen activiteiten">
                {hiddenActivities.map((a) => (
                  <li key={a.id} className="flex min-h-12 items-center justify-between gap-3 rounded-2xl px-2">
                    <span className="min-w-0 truncate text-sm font-semibold">{a.naam}</span>
                    <button
                      type="button"
                      onClick={() => {
                        unhide(a.id);
                        showToast(`${a.naam} is weer zichtbaar.`);
                      }}
                      aria-label={`Herstel ${a.naam}`}
                      className={btnTonal}
                    >
                      Herstel
                    </button>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                onClick={() => {
                  unhideAll();
                  showToast('Alle activiteiten zijn weer zichtbaar.');
                }}
                className={`${btnTonal} w-full`}
              >
                Alles herstellen
              </button>
            </Section>
          ) : null}

          <Section icon={Download} title="Snelle acties">
            <div className="space-y-1">
              <button type="button" onClick={() => setConfirmReset(true)} className={rowButton}>
                <RotateCcw size={18} aria-hidden="true" className="shrink-0 text-slate-500 dark:text-slate-400" />
                <span className="whitespace-nowrap">Standaard activiteiten herladen</span>
              </button>
              <button type="button" onClick={onExport} className={rowButton}>
                <Download size={18} aria-hidden="true" className="shrink-0 text-slate-500 dark:text-slate-400" />
                <span className="whitespace-nowrap">Exporteer JSON</span>
              </button>
              <button type="button" onClick={() => void onShare()} className={rowButton}>
                <Share2 size={18} aria-hidden="true" className="shrink-0 text-slate-500 dark:text-slate-400" />
                <span className="whitespace-nowrap">Deel activiteiten</span>
              </button>
            </div>
          </Section>

          <Section icon={CloudSun} title="Weer & locatie">
            <p role="status" aria-live="polite" className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
              <span className="font-semibold">
                {weather.label}
                {temp ? ` · ${temp}` : ''}
                {weather.kind !== 'onbekend' ? ` · wind ${wind.label}` : ''}
              </span>
              <br />
              {weatherCaption(weather, now)}
            </p>
            {weather.usesOwnLocation ? (
              <button type="button" onClick={weatherApi.useDefaultLocation} className={`${btnTonal} w-full`}>
                <MapPin size={18} aria-hidden="true" /> Terug naar Zwolle
              </button>
            ) : (
              <button type="button" onClick={() => void weatherApi.useMyLocation()} className={`${btnTonal} w-full`}>
                <LocateFixed size={18} aria-hidden="true" /> Gebruik mijn locatie voor het weer
              </button>
            )}
            {isSimulated && !devMode && (
              <button type="button" onClick={weatherApi.resetSimulation} className={`${btnTonal} w-full`}>
                <RotateCcw size={16} aria-hidden="true" /> Weer staat op simulatie — herstel live weer
              </button>
            )}
            {devMode && (
              <>
              <Disclosure icon={FlaskConical} label="Weer testen">
                <div className="flex flex-wrap gap-2" role="group" aria-label="Weertype">
                  {WEATHER_CHIPS.map(({ kind, label, icon: Icon }) => (
                    <button
                      key={kind}
                      type="button"
                      aria-pressed={activeKind === kind}
                      onClick={() => weatherApi.simulate(kind)}
                      className={choiceClass(activeKind === kind)}
                    >
                      <Icon size={16} aria-hidden="true" /> {label}
                    </button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2" role="group" aria-label="Wind">
                  {WIND_CHIPS.map(({ level, label }) => (
                    <button
                      key={level}
                      type="button"
                      aria-pressed={isSimulated && weather.windLevel === level}
                      onClick={() => weatherApi.simulateWind(level)}
                      className={choiceClass(isSimulated && weather.windLevel === level)}
                    >
                      <Wind size={16} aria-hidden="true" /> {label}
                    </button>
                  ))}
                </div>
                {isSimulated && (
                  <button type="button" onClick={weatherApi.resetSimulation} className={`${btnTonal} w-full`}>
                    <RotateCcw size={16} aria-hidden="true" /> Live weer herstellen
                  </button>
                )}
              </Disclosure>
                <button
                  type="button"
                  onClick={() => {
                    setDevMode(false);
                    showToast('Ontwikkelaarsmodus uit');
                  }}
                  className={`${btnTonal} w-full`}
                >
                  <FlaskConical size={16} aria-hidden="true" /> Ontwikkelaarsmodus uitzetten
                </button>
              </>
            )}
          </Section>

          <Disclosure icon={FileText} label="Markdown-formaat">
            <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              Gebruik deze 8 kolommen in een Markdown-tabel:
            </p>
            <pre className="overflow-x-auto rounded-2xl bg-black/5 p-3 text-xs leading-relaxed dark:bg-slate-800/80">{SAMPLE_TABLE}</pre>
            <button
              type="button"
              onClick={async () => showToast((await copyText(SAMPLE_TABLE)) ? 'Voorbeeld gekopieerd.' : 'Kopiëren is niet gelukt.')}
              className={btnTonal}
            >
              Kopieer voorbeeld
            </button>
          </Disclosure>

          <p className="pt-2 text-center text-xs text-slate-600 dark:text-slate-400">
            <BrandLink />
          </p>
          <div className="flex justify-center">
            <button
              type="button"
              onClick={() => {
                if (registerTap()) showToast(devMode ? 'Ontwikkelaarsmodus uit' : 'Ontwikkelaarsmodus aan');
              }}
              className={`min-h-11 rounded-2xl px-4 text-xs font-medium text-slate-600 dark:text-slate-400 ${focusRing}`}
            >
              Versie {packageJson.version}
            </button>
          </div>
        </div>
      </aside>

      {managing && <ActivitiesManager onClose={() => setManaging(false)} />}

      {confirmReset && (
        <Modal title="Standaard activiteiten herladen?" role="alertdialog" size="sm" onClose={() => setConfirmReset(false)}>
          <p className="text-sm leading-relaxed text-slate-700 dark:text-slate-300">
            Je lijst wordt vervangen door de standaardactiviteiten. Scores van activiteiten met dezelfde naam blijven behouden.
          </p>
          <div className="mt-5 flex flex-col gap-2">
            <button type="button" autoFocus onClick={() => void onReset()} className={btnPrimary}>
              Ja, herladen
            </button>
            <button type="button" onClick={() => setConfirmReset(false)} className={btnTonal}>
              Annuleren
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
